import { cn } from "@/shared/lib/utils";

interface KpiTileProps {
  label: string;
  value: string;
  /** The one KPI that matters gets the lime frame + lime value. */
  highlighted?: boolean;
  /** When set, the tile is an interactive filter (a button). */
  onClick?: () => void;
  /** Active filter state — lime frame + tint, aria-pressed. */
  selected?: boolean;
  "data-testid"?: string;
}

/** KPI tile (design-system.md §4). Mono 22px value + eyebrow label. Optionally a
 *  clickable filter tile (segmented-control-over-a-list pattern) when `onClick` is set. */
export function KpiTile({ label, value, highlighted, onClick, selected, ...rest }: KpiTileProps) {
  const interactive = typeof onClick === "function";
  const className = cn(
    "rounded-ge-card border px-4 py-[14px] text-left transition-colors",
    selected
      ? "border-ge-border-accent bg-ge-accent-tint"
      : highlighted
        ? "border-ge-border-accent bg-ge-card"
        : "border-ge-border-card bg-ge-card",
    interactive && !selected && "hover:bg-white/[0.04]",
  );
  const body = (
    <>
      <div
        className={cn(
          "font-mono text-[22px] font-semibold leading-none",
          selected || highlighted ? "text-ge-accent" : "text-ge-primary",
        )}
      >
        {value}
      </div>
      <div className="ge-eyebrow mt-2">{label}</div>
    </>
  );
  if (interactive) {
    return (
      <button type="button" onClick={onClick} aria-pressed={!!selected} className={className} {...rest}>
        {body}
      </button>
    );
  }
  return (
    <div className={className} {...rest}>
      {body}
    </div>
  );
}

/** A responsive strip of KPI tiles (12px gaps per the grid spec). */
export function KpiStrip({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-5">{children}</div>;
}
